import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { MenuItem, Pagination, Select, SelectProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Loader from 'react-spinners/BarLoader';
import API from 'api/API';
import Button from 'components/Button';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { DraftTile } from 'components/DraftTile';
import { useLanguage } from 'context/AppContext';
import { ITEMS_PER_PAGE } from 'utils/helpers/constants';
import * as Styles from './DraftCollection.styles';

interface DraftCollectionProps {
  sets: string[];
  devices: string[];
}

export enum DraftFilterType {
  set = 'set',
  device = 'device',
}

export const DraftCollection: FC<DraftCollectionProps> = ({ sets, devices }) => {
  const navigate = useNavigate();
  const [language] = useLanguage();
  const [page, setPage] = useState(0);
  const [draftFilter, setDraftFilter] = useState<DraftFilterType>(DraftFilterType.set);
  const [mergeAllModal, setMergeAllModal] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const activeItems = useMemo(
    () => (draftFilter === DraftFilterType.set ? sets : devices),
    [draftFilter, devices, sets],
  );
  const itemsOnPage = useMemo(
    () => activeItems.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE),
    [activeItems, page],
  );
  const filterOptionView = useCallback(
    (value: DraftFilterType): string =>
      language.DraftCollection.filterOptions[value as keyof typeof language.DraftCollection.filterOptions],
    [language],
  );

  // Read state from URL
  useEffect(() => {
    [...new URLSearchParams(window.location.search).entries()].forEach(([key, value]) => {
      switch (key) {
        case 'page':
          setPage(parseInt(value));
          break;
        case 'filter':
          setDraftFilter(value as DraftFilterType);
          break;
      }
    });
  }, []);

  // Store state in URL
  useEffect(() => {
    const params = new URLSearchParams();

    params.set('page', page.toString());
    params.set('filter', draftFilter);

    navigate(`${window.location.pathname}?${params.toString()}`);
  }, [draftFilter, navigate, page]);

  const handleActiveItemChange = useCallback<NonNullable<SelectProps['onChange']>>(event => {
    setDraftFilter(event.target.value as DraftFilterType);
    setPage(0);
  }, []);

  const mergeAll = useCallback(async () => {
    if (draftFilter === DraftFilterType.device) {
      setMergeLoading(true);
      await API.bulkUpload.mergeAllDraftParts();
      setMergeLoading(false);
    } else {
      // TODO: MF add back for sets bulk upload
      // await API.bulkUpload.mergeAllDraftSets();
    }
  }, [draftFilter]);

  return (
    <Styles.DraftSection>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px', marginBottom: '30px' }}>
        {mergeLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div style={{ marginBottom: '20px', alignContent: 'center' }}>{language.DraftCollection.mergingItems}</div>
            <Loader width="100%" />
          </div>
        )}
      </div>
      <Styles.MenuContainer>
        <div>
          <Select
            value={draftFilter}
            onChange={handleActiveItemChange}
            renderValue={value => `Filter: ${filterOptionView(value)}`}
          >
            {Object.entries(language.DraftCollection.filterOptions).map(([value, text]) => (
              <MenuItem key={value} {...{ value }}>
                {text}
              </MenuItem>
            ))}
          </Select>
          {/* TODO: get rid of this conditional so this button appears for sets and devices once set upload */}
          {draftFilter === DraftFilterType.device && (
            <Button sx={{ mb: 1, marginLeft: '20px' }} onClick={() => setMergeAllModal(true)}>
              {language.DraftCollection.mergeAll}
            </Button>
          )}
        </div>
        <div>
          {language.DraftCollection.showing
            .replace('{{itemsOnPage}}', itemsOnPage.length.toString())
            .replace('{{activeItems}}', activeItems.length.toString())}
        </div>
      </Styles.MenuContainer>
      <Styles.Divider />
      <Styles.DraftTileContainer>
        {activeItems.length !== 0 ? (
          <DraftTile draftFilter={draftFilter} itemsOnPage={itemsOnPage} />
        ) : (
          language.DraftCollection.noItems.replace('{{draftFilter}}', draftFilter)
        )}
      </Styles.DraftTileContainer>
      <Styles.Divider />
      <Styles.PaginationContainer>
        <Pagination
          shape="rounded"
          count={Math.ceil(activeItems.length / ITEMS_PER_PAGE)}
          page={page + 1}
          onChange={(_event, page) => setPage(page - 1)}
          showFirstButton
          showLastButton
        />
      </Styles.PaginationContainer>
      <ConfirmModal
        open={mergeAllModal}
        close={() => setMergeAllModal(false)}
        confirm={() => mergeAll()}
        confirmText={language.ConfirmModal.mergeAll}
        buttonText={language.ConfirmModal.confirm}
        cancelText={language.ConfirmModal.cancelText}
      />
    </Styles.DraftSection>
  );
};
